import { inject, Injectable } from '@angular/core';
import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { LanguageService } from '@uigy/shared/services/language.service';
import {
  StateIndicatorCellRendererComponent,
  StateIndicatorCellRendererParams,
} from '@uigy/shared/modules/grid/cell-renderers/state-indicator-cell-renderer/state-indicator-cell-renderer.component';
import {
  AssetField,
  AssetSourceType,
  AssetType,
  Credentials,
  ExprOp,
  OtAgentDetails,
  OtAgentScanResult,
  OtAgentStatus, PairedIcp,
} from '@uigy/generated/graphql';
import { StateIndicatorType } from '@uigy/shared/components/icons/interfaces';
import { RouterRendererComponent } from '@uigy/shared/modules/grid/cell-renderers/router-renderer/router-renderer.component';
import { RoutesStatesNamesEnum } from '@uigy/shared/enums/routesStatesNames.enum';
import { IndegyICellRendererParams, ScheduleEntry } from '@uigy/shared/models/interfaces';
import { DateTimeService } from '@uigy/shared/services/date-time/date-time.service';
import { DatesFormatsEnum } from '@uigy/shared/enums/dateFormats.enum';
import { HumanizeFrequencyService } from '@uigy/shared/components/frequency-editor/humanize-frequency.service';
import { OtAgentSelectField } from '@uigy/generated/graphql-worker';
import Utils from '@uigy/utils';
import { GridUtils } from '@uigy/shared/components/ui-grid/grid-utils';
import { UiGridFilterModel } from '@uigy/shared/components/ui-grid/models/ui-grid';
import {
  AssetTypeCellRendererComponent,
} from '@uigy/shared/components/ui-grid/components/cell-renderers/asset-type-cell-renderer/asset-type-cell-renderer.component';
import { GridFormatterService } from '@uigy/shared/modules/grid/formatters/grid-formatter.service';
import { InventoryUiGridService } from '@uigy/modules/inventory/services/inventory-ui-grid.service';
import { InventoryTabQueryParams, TabGroup, TabQueryParams } from '@uigy/shared/components/tabs/tabs.enums';
import { TimeFilterSelectorComponent } from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.component';
import { createFilterComponentMap } from '@uigy/shared/modules/grid/filters/time-filter/time-filter-selector.interfaces';
import { UiGridFiltersService } from '@uigy/shared/components/ui-grid/services/ui-grid-filters.service';
import { TextListFilterComponent } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.component';
import { TextListFilterParams } from '@uigy/shared/modules/grid/filters/text-list-filter/text-list-filter.interfaces';

@Injectable({
  providedIn: 'root',
})
export class OtAgentsGridSettingsService {
  private languageService = inject(LanguageService);
  private humanizeFrequencyService = inject(HumanizeFrequencyService);
  private gridFormatterService = inject(GridFormatterService);
  private readonly otAgentsTexts = this.languageService.texts.ot_agents;
  private readonly NANOSECONDS_PER_MILLISECOND = 1000000;

  private readonly OT_AGENT_ID: ColDef = {
    colId: OtAgentSelectField.Id,
    field: 'id',
    headerName: this.otAgentsTexts.column_defs.id,
    hide: true,
    enableRowGroup: false,
    cellRenderer: 'copyTextRendererComponent',
    filter: TextListFilterComponent,
    filterParams: {
      title: this.otAgentsTexts.column_defs.id,
      validationType: 'uuid',
    } as TextListFilterParams,
  };

  public readonly IP: ColDef = {
    headerName: this.otAgentsTexts.column_defs.ip,
    field: 'host',
    minWidth: 180,
    enableRowGroup: false,
  };

  public readonly STATUS: ColDef = {
    colId: OtAgentSelectField.Status,
    headerName: this.otAgentsTexts.column_defs.status,
    field: 'status',
    minWidth: 220,
    cellRenderer: StateIndicatorCellRendererComponent,
    valueFormatter: params => this.resolveStatusText(params.value),
    cellRendererParams: this.resolveStateIconWithText.bind(this),
    filterParams: {
      valueFormatter: (params: string) => (!params ? '' : Utils.convertPascalCaseToWords(params)),
    }
  };

  public readonly ACTIVE_QUERY_NETWORKS: ColDef = {
    headerName: this.otAgentsTexts.column_defs.active_query_networks,
    field: 'networks',
    minWidth: 180,
    enableRowGroup: false,
    filterParams: {
      customMap: GridUtils.mapFlat
    },
  };

  public readonly AGENT_NAME: ColDef = {
    headerName: this.otAgentsTexts.column_defs.name,
    field: 'name',
    minWidth: 172,
    enableRowGroup: false,
  };

  public readonly HOST_ASSET: ColDef = {
    headerName: this.otAgentsTexts.column_defs.host_asset,
    field: 'hostAssetName',
    minWidth: 180,
    cellRenderer: RouterRendererComponent,
    cellRendererParams: {
      routerLink: [RoutesStatesNamesEnum.root, RoutesStatesNamesEnum.singleAssetPage],
      queryParams: (params: IndegyICellRendererParams<OtAgentDetails>) => ({id: params.data?.hostAsset?.id}),
    },
    enableRowGroup: false,
  };

  public readonly HOST_TYPE: ColDef = {
    headerName: this.otAgentsTexts.column_defs.host_type,
    field: 'hostAssetType',
    minWidth: 180,
    enableRowGroup: false,
    hide: true,
    cellRenderer: AssetTypeCellRendererComponent,
    tooltipValueGetter: (params) => this.gridFormatterService.assetTypeFormatter(params.value),
    valueFormatter: (params) => this.gridFormatterService.assetTypeFormatter(params.value),
    filterParams: {
      valueFormatter: (params: AssetType) => this.gridFormatterService.assetTypeFormatter(params),
    },
  };

  public readonly SCAN_SCHEDULE: ColDef = {
    colId: OtAgentSelectField.Schedule,
    headerName: this.otAgentsTexts.column_defs.scan_schedule,
    field: 'schedule',
    minWidth: 200,
    filter: false,
    enableRowGroup: false,
    valueGetter: params => (params.data?.scheduleEnabled && params.data?.schedule) ? this.humanizeFrequencyService.humanize(params.data?.schedule as ScheduleEntry[], 'long'): this.languageService.texts.general.disabled,
  };

  public readonly LAST_SCAN_RESULT: ColDef = {
    headerName: this.otAgentsTexts.column_defs.last_scan_result,
    field: 'lastScanResult',
    minWidth: 220,
    valueFormatter: params => this.resolveLastScanResultText(params.value),
  };

  public readonly LAST_SCAN: ColDef = {
    headerName: this.otAgentsTexts.column_defs.last_scan,
    field: 'lastScanStartTime',
    minWidth: 220,
    filter: TimeFilterSelectorComponent,
    valueGetter: (params) => params.data?.lastScanStartTime && DateTimeService.utcToLocalFormatted(params.data?.lastScanStartTime, DatesFormatsEnum.general),
    enableRowGroup: false,
    filterParams: {
      componentMap: createFilterComponentMap(),
    }
  };

  public readonly LAST_SCAN_DURATION: ColDef = {
    headerName: this.otAgentsTexts.column_defs.last_scan_duration,
    field: 'lastScanDuration',
    valueGetter: params => params.data?.lastScanDuration ? DateTimeService.duration(params.data?.lastScanDuration / this.NANOSECONDS_PER_MILLISECOND).humanize(): '',
    minWidth: 180,
    enableRowGroup: false,
    filter: false
  };

  public readonly CREDENTIALS: ColDef = {
    headerName: this.otAgentsTexts.column_defs.credentials,
    field: 'credentials',
    minWidth: 200,
    valueGetter: (params) => {
      return (Utils.stripConnection(params?.data?.credentials) as Credentials[]).map(cred=> cred.name).join(',');
    },
    enableRowGroup: false,
    filter: false
  };

  public readonly REPORTED_ASSETS: ColDef = {
    headerName: this.otAgentsTexts.column_defs.reported_assets,
    field: 'assets',
    minWidth: 144,
    valueGetter: params => params.data?.assets?.count > 0 ? params.data?.assets?.count: '',
    enableRowGroup: false,
    filter: false,
    cellRenderer: RouterRendererComponent,
    cellRendererParams: {
      routerLink: [RoutesStatesNamesEnum.root, RoutesStatesNamesEnum.inventory],
      queryParams: (params: IndegyICellRendererParams<OtAgentDetails>) => {
        const agentName = `${params.data.name} (${AssetSourceType.OtAgent})`;
        const assetFilter = this.createAssetFilter([agentName], AssetField.Sources);
        const agGridFilter = GridUtils.convertUiGridFilterToAgGridFilter(assetFilter);
        return {
          [TabGroup.InventoryTab]: InventoryTabQueryParams.AllAssets,
          filters: UiGridFiltersService.encodeURIFilters(JSON.stringify(agGridFilter.filters)),
          sort: encodeURI(JSON.stringify(InventoryUiGridService.defaultSort)),
        };
      },
    }
  };

  public readonly AGENT_VERSION: ColDef = {
    colId: OtAgentSelectField.ShortVersion,
    headerName: this.otAgentsTexts.column_defs.agent_version,
    field: 'version',
    valueGetter: params => params.data?.version ?? params.data?.shortVersion, // on groupBy it looks for shortVersion
    minWidth: 144,
  };

  public readonly SCOUT_VERSION: ColDef = {
    colId: OtAgentSelectField.ShortScannerVersion,
    headerName: this.otAgentsTexts.column_defs.scout_version,
    field: 'scoutVersion',
    cellClass: (params) => this.scoutVersionColor(params.data?.scoutUpdateAvailable),
    valueGetter: params => this.scoutVersionFormatter(params.data?.scoutVersion ?? params.data?.shortScannerVersion, params.data?.scoutUpdateAvailable), // on groupBy it looks for shortScannerVersion
    minWidth: 200,
  };

  public readonly HOST_OS: ColDef = {
    headerName: this.otAgentsTexts.column_defs.host_os,
    field: 'hostOs',
    minWidth: 180,
    enableRowGroup: false,
  };

  public readonly NETWORK_AREA: ColDef = {
    colId: OtAgentSelectField.ReportedNetworkAreaNames,
    headerName: this.otAgentsTexts.column_defs.network_area,
    field: 'reportedNetworkAreaNames',
    minWidth: 180,
    enableRowGroup: false,
    valueGetter: params => params.data?.reportedNetworkAreaNames,
    filterParams: {
      customMap: GridUtils.mapFlat
    }
  };

  public get columnDefs(): ColDef[] {
    return [
      this.OT_AGENT_ID,
      this.IP,
      this.STATUS,
      this.LAST_SCAN_RESULT,
      this.ACTIVE_QUERY_NETWORKS,
      this.NETWORK_AREA,
      this.AGENT_NAME,
      this.HOST_ASSET,
      this.HOST_TYPE,
      this.SCAN_SCHEDULE,
      this.LAST_SCAN,
      this.LAST_SCAN_DURATION,
      this.CREDENTIALS,
      this.REPORTED_ASSETS,
      this.AGENT_VERSION,
      this.SCOUT_VERSION,
      this.HOST_OS
    ];
  }

  public otAgentsTransformFunction = (model: UiGridFilterModel<OtAgentSelectField>): UiGridFilterModel<OtAgentSelectField> => {
    if (model.field !== OtAgentSelectField.Networks && model.field !== OtAgentSelectField.ReportedNetworkAreaNames) {
      return model;
    }
    return GridUtils.convertListFilter(model);
  };

  private resolveStateIconWithText(params: ICellRendererParams): Pick<StateIndicatorCellRendererParams, 'text' | 'value'> {
    const text = this.resolveStatusText(params.value);
    switch (params.value) {
      case OtAgentStatus.WaitingForConnection:
        return {
          value: StateIndicatorType.Disconnected,
          text: text
        };
      case OtAgentStatus.ManuallyRegistered:
        return {
          value: StateIndicatorType.Connected,
          text: text
        };
      case OtAgentStatus.Connected:
        return {
          value: StateIndicatorType.Connected,
          text: text
        };
      case OtAgentStatus.PreparingConnection:
        return {
          value: StateIndicatorType.Pending,
          text: text
        };
      case OtAgentStatus.Disconnected:
        return {
          value: StateIndicatorType.Disconnected,
          text: text
        };
      case OtAgentStatus.PendingApproval:
      case OtAgentStatus.PendingConfiguration:
        return {
          value: StateIndicatorType.Pending,
          text: text
        };
      case OtAgentStatus.Updating:
      case OtAgentStatus.Scanning:
        return {
          value: StateIndicatorType.Running,
          text: text
        };
      default:
        return {
          value: StateIndicatorType.Disconnected,
          text: text
        };
    }
  }
  private resolveStatusText(status: OtAgentStatus): string {
    switch (status) {
      case OtAgentStatus.WaitingForConnection:
        return this.otAgentsTexts.state_options.waiting_for_connection;
      case OtAgentStatus.ManuallyRegistered:
        return this.otAgentsTexts.state_options.manually_registered;
      case OtAgentStatus.PreparingConnection:
        return this.otAgentsTexts.state_options.preparing_connection;
      case OtAgentStatus.Disconnected:
        return this.otAgentsTexts.state_options.disconnected;
      case OtAgentStatus.Connected:
        return this.otAgentsTexts.state_options.connected;
      case OtAgentStatus.PendingApproval:
        return this.otAgentsTexts.state_options.pending_approval;
      case OtAgentStatus.PendingConfiguration:
        return this.otAgentsTexts.state_options.pending_configuration;
      case OtAgentStatus.Scanning:
        return this.otAgentsTexts.state_options.scanning;
      case OtAgentStatus.Updating:
        return this.otAgentsTexts.state_options.updating;
      default:
        return this.otAgentsTexts.state_options.no_status;
    }
  }

  private resolveLastScanResultText(scanResult: OtAgentScanResult): string {
    switch (scanResult) {
      case OtAgentScanResult.Completed:
        return this.otAgentsTexts.last_scan_result_options.completed;
      case OtAgentScanResult.Failed:
        return this.otAgentsTexts.last_scan_result_options.failed;
      default:
        return '';
    }
  }

  private createAssetFilter(value: string[], field: AssetField): UiGridFilterModel<AssetField> {
    return {
      op: ExprOp.And,
      expressions: [
        {field, op: ExprOp.Equal, values: value},
        {field: AssetField.Hidden, op: ExprOp.Equal, values: false},
      ],
    };
  }

  private scoutVersionFormatter(version: string, updateAvailable: boolean): string {
    return updateAvailable ? `${version} (${this.otAgentsTexts.update_agent.update_available})` : version;
  }

  private scoutVersionColor(updateAvailable: boolean): string {
    return updateAvailable ? 'g-red' : '';
  }
}
